import React, { useEffect, useRef, useState } from 'react';
import { useNotifications } from '../Notifications/NotificationProvider';
import { adminApi } from '../../services/api';

type Props = {
  username?: string | null;
  email?: string | null;
  isModerator?: boolean;
  onOpenProfile?: () => void;
  onOpenMessages?: () => void;
  onLogout?: () => void;
  unreadMessages?: number;
  onShowUserMenu?: () => void;
};

const UserIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const BellIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 01-3.46 0"/>
  </svg>
);

const MessageIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const ShieldIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const SignOutIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const SettingsIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="3"/>
    <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
  </svg>
);


const UserMenu: React.FC<Props> = ({ username, email, isModerator, onOpenProfile, onOpenMessages, onLogout, unreadMessages = 0, onShowUserMenu }) => {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({ right: 0 });
  const ref = useRef<HTMLDivElement | null>(null);
  const { persistentUnreadCount } = useNotifications();
  const [pendingReports, setPendingReports] = useState<number>(0);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  // Poll pending reports for moderators
  useEffect(() => {
    if (!isModerator) { setPendingReports(0); return; }
    let timer: any;
    const fetchReports = async () => {
      try {
        const r = await adminApi.getReports();
        setPendingReports(Array.isArray(r.data) ? r.data.length : 0);
      } catch {}
    };
    fetchReports();
    timer = setInterval(fetchReports, 30000);
    return () => timer && clearInterval(timer);
  }, [isModerator]);

  // Enhanced dropdown positioning with mobile and z-index considerations
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const adjust = () => {
      const el = dropdownRef.current;
      if (!el) return;
      
      const buttonRect = ref.current?.getBoundingClientRect();
      if (!buttonRect) return;
      
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const pad = 8;
      const dropdownWidth = 280;
      
      // Calculate positioning
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const spaceRight = viewportWidth - buttonRect.right;
      const spaceLeft = buttonRect.left;
      
      let newStyle: React.CSSProperties = {
        position: 'fixed', // Use fixed positioning to escape stacking context issues
        minWidth: dropdownWidth,
        maxWidth: Math.min(dropdownWidth, viewportWidth - 2 * pad),
        zIndex: 99999, // Even higher z-index
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        border: '1px solid rgba(0,0,0,0.1)',
        backgroundColor: 'white', // Ensure solid background
        borderRadius: '8px'
      };
      
      // Determine vertical positioning (prefer below, but go above if not enough space)
      if (spaceBelow >= 200 || spaceBelow > spaceAbove) {
        // Position below button using fixed coordinates
        newStyle.top = buttonRect.bottom + 4 + 'px';
        newStyle.bottom = 'auto';
      } else {
        // Position above button using fixed coordinates
        newStyle.bottom = (viewportHeight - buttonRect.top + 4) + 'px';
        newStyle.top = 'auto';
      }
      
      // Determine horizontal positioning using fixed coordinates
      if (spaceRight >= dropdownWidth) {
        // Align to right edge of button (default)
        newStyle.right = (viewportWidth - buttonRect.right) + 'px';
        newStyle.left = 'auto';
      } else if (spaceLeft >= dropdownWidth) {
        // Align to left edge of button
        newStyle.left = buttonRect.left + 'px';
        newStyle.right = 'auto';
      } else {
        // Center horizontally but keep within viewport
        const idealLeft = buttonRect.left - (dropdownWidth - buttonRect.width) / 2;
        const constrainedLeft = Math.max(pad, Math.min(idealLeft, viewportWidth - dropdownWidth - pad));
        newStyle.left = constrainedLeft + 'px';
        newStyle.right = 'auto';
      }
      
      setDropdownStyle(newStyle);
    };
    
    // Adjust immediately and on resize
    adjust();
    const t = window.setTimeout(adjust, 0);
    window.addEventListener('resize', adjust);
    window.addEventListener('scroll', adjust, { passive: true });
    
    return () => { 
      window.clearTimeout(t); 
      window.removeEventListener('resize', adjust); 
      window.removeEventListener('scroll', adjust);
    };
  }, [open]);

  const label = username || email || 'Account';
  const totalCount = persistentUnreadCount + (unreadMessages || 0) + (isModerator ? pendingReports : 0);
  const badgeBreakdown = [] as string[];
  if (persistentUnreadCount > 0) badgeBreakdown.push(`Notifications: ${persistentUnreadCount}`);
  if ((unreadMessages || 0) > 0) badgeBreakdown.push(`Messages: ${unreadMessages}`);
  if (isModerator && pendingReports > 0) badgeBreakdown.push(`Reports: ${pendingReports}`);
  const badgeTitle = badgeBreakdown.length ? badgeBreakdown.join(' • ') : label;

  return (
    <div ref={ref} className="user-menu" style={{ position: 'relative', display: 'inline-block' }}>
      <button className="notif-bell-btn" onClick={() => onShowUserMenu ? onShowUserMenu() : setOpen(v => !v)} aria-haspopup="true" aria-expanded={open} title={badgeTitle} style={{ position: 'relative' }}>
        <UserIcon />
        {totalCount > 0 && <span className="profile-badge">{totalCount}</span>}
      </button>
      {open && (
        <div ref={dropdownRef} className="notif-dropdown user-menu" role="menu" style={dropdownStyle}>
          {/* Main menu view only; notifications open in modal */}
          <>
              <div className="menu-header">
                Signed in as <strong>{label}</strong>
              </div>

              <div className="menu-list">
                <button className="menu-item" onClick={() => { setOpen(false); onOpenProfile?.(); }}>
                  <UserIcon /> <span>View My Profile</span>
                </button>
                <a className="menu-item" href="/settings" onClick={() => setOpen(false)}>
                  <SettingsIcon /> <span>Account Settings</span>
                </a>
                <button className="menu-item" onClick={() => { setOpen(false); onOpenMessages?.(); }} style={{ justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <MessageIcon /> <span>Messages</span>
                  </div>
                  {unreadMessages > 0 && (
                    <span style={{ background: '#ef4444', color: 'white', borderRadius: '9999px', fontSize: '11px', padding: '2px 6px', minWidth: '18px', textAlign: 'center' }}>
                      {unreadMessages}
                    </span>
                  )}
                </button>
                 <button 
                   className="menu-item" 
                   onClick={(e) => {
                     e.preventDefault();
                     e.stopPropagation();
                    // Open profile modal and show notifications tab
                    try { (window as any).__profileInitialTab = 'notifications'; } catch {}
                    setOpen(false);
                    onOpenProfile?.();
                   }} 
                   style={{ justifyContent: 'space-between', cursor: 'pointer' }}
                 >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <BellIcon /> <span>Notifications</span>
                  </div>
                  {persistentUnreadCount > 0 && (
                    <span style={{ background: '#ef4444', color: 'white', borderRadius: '9999px', fontSize: '11px', padding: '2px 6px', minWidth: '18px', textAlign: 'center' }}>
                      {persistentUnreadCount}
                    </span>
                  )}
                </button>
                {isModerator && (
                  <a className="menu-item" href="/admin" onClick={() => { setOpen(false); setPendingReports(0); }} style={{ textDecoration:'none', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:10 }}>
                      <ShieldIcon /> <span>Moderation</span>
                    </span>
                    {pendingReports > 0 && (
                      <span style={{ background: '#dc2626', color: '#fff', borderRadius: 9999, fontSize: 11, padding: '2px 6px', minWidth: 18, textAlign: 'center' }} title={`${pendingReports} pending reports`}>
                        {pendingReports}
                      </span>
                    )}
                  </a>
                )}
              </div>

              <div className="menu-divider"></div>

              <div>
                <button className="menu-item menu-danger" onClick={() => { setOpen(false); onLogout?.(); }}>
                  <SignOutIcon /> <span>Sign Out</span>
                </button>
              </div>
            </>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
